import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Activity, ExternalLink } from "lucide-react";
import { fmtBytes, fmtDate, useApi } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";

export default function TeamActivity() {
  const api = useApi();
  const [who, setWho] = useState("all");
  const { data, isLoading, isError } = useQuery({ queryKey: ["firm-activity"], queryFn: async () => (await api.get("/firm/activity")).data });
  const rows = useMemo(() => (data?.exports || []).filter((e) => who === "all" || e.email === who), [data, who]);

  if (isLoading) return <div className="h-40 bg-white border border-slate-200 rounded-lg animate-pulse" />;
  if (isError || !data) return <div className="bg-white border border-red-200 text-red-700 rounded-lg p-5 text-sm" data-testid="activity-error">Couldn't load team activity. Try refreshing the page.</div>;
  return (
    <section className="space-y-4" data-testid="team-activity">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h2 className="font-heading font-bold text-xl flex items-center gap-2"><Activity size={18} className="text-blue-600" /> Team activity</h2>
          <p className="text-sm text-slate-600">Who exported which case, newest first.</p>
        </div>
        <select data-testid="activity-member-filter" value={who} onChange={(e) => setWho(e.target.value)} className="h-10 px-3 rounded-md border border-slate-200 bg-white text-sm">
          <option value="all">Everyone</option>
          {data.members.map((m) => <option key={m.user_id} value={m.email}>{m.email}</option>)}
        </select>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="activity-member-stats">
        {data.members.map((m) => (
          <div key={m.user_id} className="bg-white border border-slate-200 rounded-lg px-4 py-3" data-testid={`activity-member-${m.user_id}`}>
            <div className="font-semibold text-sm truncate">{m.email}</div>
            <div className="text-xs text-slate-500 mt-1 tabular-nums">
              {m.exports_this_month} this month · {m.exports_total} total{m.last_export_at ? ` · last ${fmtDate(m.last_export_at)}` : ""}
            </div>
          </div>
        ))}
      </div>
      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]" data-testid="activity-table">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left font-medium px-4 py-3">Teammate</th>
              <th className="text-left font-medium px-4 py-3">Case</th>
              <th className="text-left font-medium px-4 py-3">When</th>
              <th className="text-right font-medium px-4 py-3">Files</th>
              <th className="text-left font-medium px-4 py-3">Status</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((e) => (
              <tr key={e.id} data-testid={`activity-row-${e.id}`}>
                <td className="px-4 py-3 max-w-[180px] truncate">{e.email}</td>
                <td className="px-4 py-3 max-w-xs">
                  <div className="font-medium line-clamp-1">{e.case_caption || "Untitled case"}</div>
                  {e.index_number && <div className="font-mono text-xs text-amber-700">Index # {e.index_number}</div>}
                </td>
                <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{fmtDate(e.created_at)}</td>
                <td className="px-4 py-3 text-right tabular-nums">{e.downloaded ?? "-"}/{e.selected}{e.bytes ? <span className="text-slate-400"> · {fmtBytes(e.bytes)}</span> : null}</td>
                <td className="px-4 py-3">{e.status === "in progress" ? <span className="text-xs text-slate-500">In progress</span> : <StatusBadge status={e.status} />}</td>
                <td className="px-4 py-3 text-right">
                  {e.url && <a href={e.url} target="_blank" rel="noreferrer" data-testid={`activity-open-${e.id}`} className="p-2 inline-flex rounded-md text-slate-500 hover:bg-slate-100 hover:text-blue-600"><ExternalLink size={14} /></a>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && <div className="py-10 text-center text-slate-500 text-sm" data-testid="activity-empty">No exports yet.</div>}
      </div>
    </section>
  );
}
